import * as React from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import {
    AmountFormatCell,
    DateFormatCell,
    AmountFormat,
} from "components/common/formatter";
import { useState, useEffect, useMemo } from "react";
import { CrossIcon } from "components/tabs/TabTitle";
import { orderBy } from "@progress/kendo-data-query";
import editIcon from "assets/common/editBoxIcon.png";
import BillItemEditDialog from "./BillItemEditDialog";
import { FaMinus, FaPlus } from "react-icons/fa";
import styles from "./Bills.module.scss";

const RECORD_TYPE_LABELS = {
    Time: "Time",
    Disbursement: "Disbursement",
    Expense: "Expense",
};

const resolveFeEarnerLabel = (feKey, activeFeeEarners) => {
    if (!feKey) return "Unassigned";
    const fe = activeFeeEarners.find((f) => f.userRef === feKey);
    return fe?.displayname ? `${feKey} - ${fe.displayname}` : feKey;
};

const stripGridFields = (item) => {
    const {
        recordType,
        recordTypeSortOrder,
        sourceIndex,
        id,
        displayValue,
        displayNet,
        displayVat,
        displayFeeEarner,
        displayNominal,
        ...cleanItem
    } = item;
    return cleanItem;
};

const isItemEditable = (item) => {
    const recordType = item?.recordType;
    return (
        recordType === "Time" ||
        recordType === "Expense" ||
        recordType === "Disbursement"
    );
};

const matchesTimeRecord = (record, updatedItem, index) => {
    const sourceIndex = Number(updatedItem?.sourceIndex);
    const generatedId = `time-${record.timeId || record.fixedFeeItemID || index}`;
    if (updatedItem.id && generatedId === updatedItem.id) {
        return true;
    }
    if (updatedItem.timeId && record.timeId) {
        return String(record.timeId) === String(updatedItem.timeId);
    }
    if (updatedItem.fixedFeeItemID && record.fixedFeeItemID) {
        return String(record.fixedFeeItemID) === String(updatedItem.fixedFeeItemID);
    }
    return Number.isFinite(sourceIndex) ? index === sourceIndex : false;
};

const matchesDisbRecord = (record, updatedItem, index) => {
    const sourceIndex = Number(updatedItem?.sourceIndex);
    const generatedId = `disb-${record.timeId || record.postingId || index}`;
    if (updatedItem.id && generatedId === updatedItem.id) {
        return true;
    }
    const updatedId = updatedItem.postingId || updatedItem.timeId;
    const recordId = record.postingId || record.timeId;
    if (updatedId && recordId) {
        return String(recordId) === String(updatedId);
    }
    return Number.isFinite(sourceIndex) ? index === sourceIndex : false;
};

const mapDisplayFields = (item) => {
    const isExpense = item.recordType === "Expense";
    const isTime = item.recordType === "Time";
    const billNet =
        item.billedNetAmount === "w/o"
            ? "w/o"
            : isTime
                ? (typeof item.billedNetAmount === "number"
                    ? item.billedNetAmount
                    : item.value)
                : item.netAmount;

    return {
        ...item,
        displayValue: isTime ? item.value : item.netAmount,
        displayNet: billNet,
        displayVat: isTime ? item.billVat : item.vatAmount,
        displayFeeEarner: isTime
            ? (item.billFeeEarnerRef || item.feeEarnerRef)
            : item.feeEarnerRef,
        displayNominal: item.nominalRef,
        recordTypeSortOrder:
            item.recordType === "Time" ? 1 : isExpense ? 3 : 2,
    };
};

const applyEditWithFallbacks = (records, matcherItem, payloadItem, isTime) => {
    let changed = false;
    const next = records.map((record, index) => {
        let matched = false;
        if (isTime) {
            matched = matchesTimeRecord(record, matcherItem, index);
        } else {
            matched = matchesDisbRecord(record, matcherItem, index);
        }
        if (!matched) return record;
        changed = true;
        return { ...record, ...payloadItem };
    });
    return { next, changed };
};

//This is combine grid for Bill Disbursement & Time records
const BillGrid = (props) => {
    const {
        timeData = [],
        disbData = [],
        reset,
        updateTimeData,
        updateDisbData,
        changeTimeTotals,
        changeDisbTotals,
        changeExpenseTotals,
        changeBillTimeData,
        changeBillDisbData,
        activeFeeEarners = [],
        nominalVatValue,
        readOnly = false,
    } = props;

    const [combinedData, setCombinedData] = useState([]);
    const [dataState, setDataState] = useState({
        sort: [{ field: "date", dir: "asc" }],
    });
    const [collapsedFeGroups, setCollapsedFeGroups] = useState(() => new Set());
    const [editingItem, setEditingItem] = useState(null);

    const calculateNetAmount = (item) => {
        return item.billedNetAmount === "w/o"
            ? 0
            : typeof item.billedNetAmount === "number"
                ? item.billedNetAmount
                : item.value;
    };

    useEffect(() => {
        const timeRecords = timeData.map((item, index) => ({
            ...item,
            recordType: "Time",
            recordTypeSortOrder: 1,
            sourceIndex: index,
            id: `time-${item.timeId || item.fixedFeeItemID || index}`,
            displayValue: item.value,
            // If billedNetAmount is explicitly "w/o", keep it; if it's a number, use it; otherwise fallback to value
            displayNet: item.billedNetAmount === "w/o" ? "w/o" : calculateNetAmount(item),
            displayVat: item.billVat,
            // Map fee earner - prefer billFeeEarnerRef (from Time records), fallback to feeEarnerRef (from Profit Cost)
            displayFeeEarner: item.billFeeEarnerRef || item.feeEarnerRef,
            displayNominal: item.nominalRef,
        }));

        const disbRecords = disbData.map((item, index) => ({
            ...item,
            recordType: item.type === "Expense" ? "Expense" : "Disbursement",
            recordTypeSortOrder: item.type === "Expense" ? 3 : 2,
            sourceIndex: index,
            id: `disb-${item.timeId || item.postingId || index}`,
            displayValue: item.netAmount,
            displayNet: item.netAmount,
            displayVat: item.vatAmount,
            displayFeeEarner: item.feeEarnerRef,
            displayNominal: item.nominalRef,
        }));

        setCombinedData([...timeRecords, ...disbRecords]);
    }, [timeData, disbData]);

    useEffect(() => {
        setCombinedData([]);
    }, [reset]);

    useEffect(() => {
        let timeAmount = {
            totalAmount: 0,
            totalVat: 0,
        };

        if (timeData.length > 0) {
            timeData.forEach((item) => {
                const netAmount = calculateNetAmount(item);
                timeAmount.totalAmount += netAmount || 0;
                timeAmount.totalVat += item.billVat || 0;
            });
        }

        if (changeTimeTotals) {
            changeTimeTotals(timeAmount);
        }

        let finalData = [];
        timeData.forEach((item) => {
            let datatemp;
            const isWriteOff = item.billedNetAmount === "w/o";
            const netAmount = calculateNetAmount(item);

            if (item.type !== "ProfitCost") {
                datatemp = {
                    timeId: item.timeId,
                    date: item.date,
                    details: item.details,
                    netAmount: netAmount,
                    vatAmount: item.billVat,
                    vatCode: item.vatCode,
                    feeEarnerRef: item.billFeeEarnerRef,
                    nominalRef: item.nominalRef,
                };
                if (isWriteOff) {
                    datatemp.isWriteOff = true;
                }
            } else if (item.type === "ProfitCost") {
                datatemp = {
                    date: item.date,
                    details: item.details,
                    netAmount: netAmount,
                    vatAmount: item.billVat,
                    vatCode: item.vatCode,
                    feeEarnerRef: item.billFeeEarnerRef,
                    nominalRef: item.nominalRef,
                    type: item.type,
                };
                if (isWriteOff) {
                    datatemp.isWriteOff = true;
                }
            }
            finalData.push(datatemp);
        });
        if (changeBillTimeData) {
            changeBillTimeData(finalData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeData]);

    useEffect(() => {
        let disbAmount = {
            totalAmount: 0,
            totalVat: 0,
        };

        let expenseAmount = {
            totalAmount: 0,
            totalVat: 0,
        };

        if (disbData.length > 0) {
            disbData.forEach((item) => {
                if (item.type === "Expense") {
                    expenseAmount.totalAmount += item.netAmount || 0;
                    expenseAmount.totalVat += item.vatAmount || 0;
                } else {
                    disbAmount.totalAmount += item.netAmount || 0;
                    disbAmount.totalVat += item.vatAmount || 0;
                }
            });
        }

        if (changeDisbTotals) {
            changeDisbTotals(disbAmount);
        }

        if (changeExpenseTotals) {
            changeExpenseTotals(expenseAmount);
        }

        let finalData = [];
        disbData.forEach((item) => {
            if (item.type === "Expense") {
                const datatemp = {
                    date: item.date,
                    details: item.details,
                    netAmount: item.netAmount,
                    vatAmount: item.vatAmount,
                    vatCode: item.vatCode,
                    feeEarnerRef: item.feeEarnerRef,
                    nominalRef: item.nominalRef,
                    type: item.type,
                };
                finalData.push(datatemp);
            } else {
                const datatemp = {
                    postingId: item.postingId,
                    date: item.date,
                    details: item.details,
                    netAmount: item.netAmount,
                    vatAmount: item.vatAmount,
                    vatCode: item.vatCode,
                    disbursementType:
                        item.postingType ?? item.type ?? null,
                };
                finalData.push(datatemp);
            }
        });
        if (changeBillDisbData) {
            changeBillDisbData(finalData);
        }
    }, [disbData]);


    const disabledActionStyle = {
        opacity: 0.35,
        cursor: "not-allowed",
        pointerEvents: "none",
    };

    const EditCommandCell = (props) => {
        const item = props.dataItem;

        if (!isItemEditable(item)) {
            return <td />;
        }

        return (
            <td>
                <div className="d-flex justify-content-center align-items-center h-100">
                    <img
                        className={readOnly ? "" : "cursor-pointer"}
                        onClick={
                            readOnly ? undefined : () => setEditingItem(item)
                        }
                        src={editIcon}
                        style={{
                            width: "16px",
                            height: "16px",
                            ...(readOnly ? disabledActionStyle : {}),
                        }}
                        alt="Edit"
                        title={readOnly ? "Edit disabled" : "Edit item"}
                    />
                </div>
            </td>
        );
    };

    const DeleteCommandCell = (props) => {
        const item = props.dataItem;

        return (
            <td>
                <div className="d-flex justify-content-center align-items-center h-100">
                    <CrossIcon
                        disabled={readOnly}
                        onClick={(e) => {
                            if (item.recordType === "Time") {
                                const updatedData = timeData.filter((record) => {
                                    // Handle records with timeId
                                    if (item.timeId && record.timeId) {
                                        return record.timeId !== item.timeId;
                                    }
                                    // Handle records with fixedFeeItemID (from Fixed Fee popup)
                                    if (item.fixedFeeItemID && record.fixedFeeItemID) {
                                        return record.fixedFeeItemID !== item.fixedFeeItemID;
                                    }
                                    if (
                                        item.sourceIndex !== undefined &&
                                        timeData[item.sourceIndex] === record
                                    ) {
                                        return false;
                                    }
                                    // If neither match, keep the record
                                    return true;
                                });
                                if (updateTimeData) {
                                    updateTimeData(updatedData);
                                }
                            } else if (item.recordType === "Disbursement" || item.recordType === "Expense") {
                                const updatedData = disbData.filter(
                                    (record) => {
                                        const disbId = record.timeId || record.postingId;
                                        const itemId = item.timeId || item.postingId;
                                        if (
                                            !itemId &&
                                            item.sourceIndex !== undefined &&
                                            disbData[item.sourceIndex] === record
                                        ) {
                                            return false;
                                        }
                                        return disbId !== itemId;
                                    }
                                );
                                if (updateDisbData) {
                                    updateDisbData(updatedData);
                                }
                            }
                        }}
                    />
                </div>
            </td>
        );
    };

    const handleSaveEditedItem = (updatedItem) => {
        const sourceIndex = Number(updatedItem?.sourceIndex);
        const cleanedItem = stripGridFields(updatedItem);
        if (updatedItem.recordType === "Time") {
            const { next, changed } = applyEditWithFallbacks(
                timeData,
                updatedItem,
                cleanedItem,
                true,
            );
            const updatedTimeData = changed
                ? next
                : timeData.map((record, index) =>
                    Number.isFinite(sourceIndex) && index === sourceIndex
                        ? { ...record, ...cleanedItem }
                        : record,
                );
            updateTimeData?.(updatedTimeData);
        } else {
            const { next, changed } = applyEditWithFallbacks(
                disbData,
                updatedItem,
                cleanedItem,
                false,
            );
            const updatedDisbData = changed
                ? next
                : disbData.map((record, index) =>
                    Number.isFinite(sourceIndex) && index === sourceIndex
                        ? { ...record, ...cleanedItem }
                        : record,
                );
            updateDisbData?.(updatedDisbData);
        }

        setCombinedData((prev) =>
            prev.map((row) =>
                row.id === updatedItem.id ? mapDisplayFields({ ...row, ...updatedItem }) : row,
            ),
        );

        setEditingItem(null);
    };

    const HeaderCrossCell = () => {
        return (
            <div className="d-flex justify-content-center align-items-center h-100">
                <CrossIcon
                    disabled={readOnly}
                    onClick={() => {
                        if (updateTimeData) {
                            updateTimeData([]);
                        }
                        if (updateDisbData) {
                            updateDisbData([]);
                        }
                    }}
                />
            </div>
        );
    };

    const BillNetCell = (props) => {
        const value = props.dataItem.displayNet;

        if (value === "w/o") {
            return (
                <td style={{ textAlign: "center" }}>
                    w/o
                </td>
            );
        }
        return (
            <td style={{ textAlign: "center" }}>
                {typeof value === 'number' ? value.toFixed(2) : (value !== null && value !== undefined ? value : '0.00')}
            </td>
        );
    };

    const displayRows = useMemo(() => {
        const buckets = { Time: [], Disbursement: [], Expense: [] };
        combinedData.forEach((row) => {
            if (buckets[row.recordType]) {
                buckets[row.recordType].push(row);
            }
        });

        const rows = [];

        Object.keys(buckets).forEach((recordType) => {
            const bucketRows = buckets[recordType];
            if (bucketRows.length === 0) return;

            const sortedRows = orderBy(bucketRows, dataState.sort ?? []);

            rows.push({
                __rowKind: "typeHeader",
                id: `type-${recordType}`,
                label: RECORD_TYPE_LABELS[recordType] ?? recordType,
            });

            sortedRows.forEach((row) => {
                rows.push({ ...row, __rowKind: "dataRow" });
            });
            // TEMP: fee-earner grouping for Time records disabled — flat list for now
            // if (recordType !== "Time") {
            //     return;
            // }

            // const groups = new Map();
            // sortedRows.forEach((row) => {
            //     const feKey = row.displayFeeEarner || "__unassigned__";
            //     if (!groups.has(feKey)) {
            //         groups.set(feKey, []);
            //     }
            //     groups.get(feKey).push(row);
            // });

            // groups.forEach((groupRows, feKey) => {
            //     const aggregates = groupRows.reduce(
            //         (acc, row) => {
            //             acc.value += Number(row.displayValue) || 0;
            //             acc.net +=
            //                 row.displayNet === "w/o" ? 0 : Number(row.displayNet) || 0;
            //             acc.vat += Number(row.displayVat) || 0;
            //             return acc;
            //         },
            //         { value: 0, net: 0, vat: 0 },
            //     );

            //     const expanded = !collapsedFeGroups.has(feKey);

            //     rows.push({
            //         __rowKind: "feGroupHeader",
            //         id: `fe-group-${feKey}`,
            //         feKey,
            //         label: resolveFeEarnerLabel(
            //             feKey === "__unassigned__" ? null : feKey,
            //             activeFeeEarners,
            //         ),
            //         displayValue: aggregates.value,
            //         displayNet: aggregates.net,
            //         displayVat: aggregates.vat,
            //         expanded,
            //     });

            //     if (expanded) {
            //         groupRows.forEach((row) => {
            //             rows.push({ ...row, __rowKind: "dataRow" });
            //         });
            //     }
            // });
        });

        return rows;
    }, [combinedData, dataState.sort, collapsedFeGroups, activeFeeEarners]);

    const toggleFeGroup = (feKey) => {
        setCollapsedFeGroups((prev) => {
            const next = new Set(prev);
            if (next.has(feKey)) {
                next.delete(feKey);
            } else {
                next.add(feKey);
            }
            return next;
        });
    };

    const customRowRender = (row, rowProps) => {
        const kind = rowProps.dataItem.__rowKind;

        if (kind === "typeHeader") {
            return (
                <tr style={{ backgroundColor: "#f8f8f8" }}>
                    <td
                        colSpan={9}
                        style={{
                            fontWeight: 800,
                            paddingLeft: "20px",
                            fontSize: "14px",
                            textAlign: "left",
                            color: "#000000",
                        }}
                    >
                        {rowProps.dataItem.label}
                    </td>
                </tr>
            );
        }

        if (kind === "feGroupHeader") {
            const item = rowProps.dataItem;
            return (
                <tr
                    className={styles["fe-group-header-row"]}
                    onClick={() => toggleFeGroup(item.feKey)}
                >
                    <td className={styles["fe-group-toggle-cell"]}>
                        {item.expanded ? <FaMinus /> : <FaPlus />}
                    </td>
                    <td>{item.label}</td>
                    <td />
                    <td />
                    <td style={{ textAlign: "center" }}>
                        {AmountFormat(item.displayValue)}
                    </td>
                    <td style={{ textAlign: "center" }}>
                        {AmountFormat(item.displayNet)}
                    </td>
                    <td style={{ textAlign: "center" }}>
                        {AmountFormat(item.displayVat)}
                    </td>
                    <td />
                    <td />
                </tr>
            );
        }

        return row;
    };

    return (
        <>
            <Grid
                data={displayRows}
                {...dataState}
                onDataStateChange={(e) => setDataState(e.dataState)}
                sortable={true}
                rowRender={customRowRender}
                className="h-100 overflow-auto fs-sm grid-minwidth-col3-200"
            >
                <Column
                    field="date"
                    title="Date"
                    editable={false}
                    cell={DateFormatCell}
                    width="100px"
                    className="ps-3"
                />
                <Column
                    field="details"
                    title="Details"
                    editable={false}
                    width="200px"
                    className="ps-0"
                />
                <Column
                    field="displayFeeEarner"
                    title="FE"
                    editable={false}
                    width="60px"
                />
                <Column
                    field="displayNominal"
                    title="Nominal"
                    editable={false}
                    width="80px"
                />
                <Column
                    field="displayValue"
                    title="Value"
                    editable={false}
                    width="80px"
                    cell={AmountFormatCell}
                    headerClassName="grid-header-center"
                />

                <Column
                    field="displayNet"
                    title="Bill Net"
                    editable={false}
                    width="80px"
                    cell={BillNetCell}
                    headerClassName="grid-header-center"
                />

                <Column
                    field="displayVat"
                    title="Bill Vat"
                    editable={false}
                    width="80px"
                    cell={AmountFormatCell}
                    headerClassName="grid-header-center"
                />
                <Column cell={EditCommandCell} title="" width={30} />
                <Column
                    cell={DeleteCommandCell}
                    headerCell={HeaderCrossCell}
                    width={45}
                />
            </Grid>
            {editingItem && (
                <BillItemEditDialog
                    item={editingItem}
                    feeEarners={activeFeeEarners}
                    defaultVatCode={nominalVatValue?.vatCode}
                    onClose={() => setEditingItem(null)}
                    onSave={handleSaveEditedItem}
                />
            )}
        </>
    );
};

export default BillGrid;

